import { FlagType } from '@/types';

export type ChariotModuleOption = {
  name: string;
  description: string;
  value: FlagType;
  positiveFlag?: boolean;
  /** Flag value this option depends on being enabled */
  dependsOn?: FlagType;
  /** Human-readable name of the dependency for the hint message */
  dependencyLabel?: string;
  /** If true, the dependency uses positive flag logic (present = enabled) */
  dependsOnPositiveFlag?: boolean;
  /** If set, shows a confirmation dialog when enabling this feature */
  confirmationMessage?: string;
  /** If true, the toggle is permanently disabled with a "Coming Soon" label */
  comingSoon?: boolean;
};

export const chariotModuleOptions: ChariotModuleOption[] = [
  {
    name: 'Static IP Addresses',
    description: 'Enables scanning from a static IP pool',
    value: FlagType.DISABLE_STATIC_IP_ADDRESSES,
  },
  {
    name: 'AI Inference',
    description:
      'Human in the loop. Answers questions using your data; you act on the results.',
    value: FlagType.DISABLE_AI_INFERENCE,
    confirmationMessage:
      'Enabling AI Inference allows the AI Assistant to process and respond to queries using your account data.',
  },
  {
    name: 'AI Agents',
    description:
      'Human on the loop. Acts on your behalf while you supervise and can intervene.',
    value: FlagType.ENABLE_AI_AGENTS,
    positiveFlag: true,
    dependsOn: FlagType.DISABLE_AI_INFERENCE,
    dependencyLabel: 'AI Inference',
    confirmationMessage:
      'Enabling AI Agents allows the AI Assistant to autonomously perform actions on your behalf, such as triaging vulnerabilities, validating seeds, and even targeting assets for compromise.',
  },
  {
    name: 'AI Autonomy',
    description:
      'Human off the loop. Fully autonomous penetration testing via Hannibal.',
    // Positive flag: presence = ON, default OFF. Hunt visibility is enforced
    // server-side (my.go HuntsVisible: flag AND analyst/admin role) — this
    // toggle only flips the org flag the server reads. It is independent of the
    // AI Inference/Agents chain, so no dependsOn.
    value: FlagType.ENABLE_AI_AUTOMATION,
    positiveFlag: true,
    confirmationMessage:
      'Enabling AI Autonomy allows admin and analyst users on this account to view hunt sessions. Visibility is enforced server-side; users with the read-only role will not see hunts even while this is on.',
  },
  {
    name: 'AI Training',
    description: 'Enables AI model training on account data',
    value: FlagType.DISABLE_AIMI_TRAINING,
    dependsOn: FlagType.ENABLE_AI_AGENTS,
    dependencyLabel: 'AI Agents',
    dependsOnPositiveFlag: true,
    confirmationMessage:
      'Enabling AI Training allows your account data to be used for improving AI model performance. Your data will be processed in accordance with our data handling policies.',
  },
];

/** The Static IP toggle, rendered standalone below Allowed Email Domains. */
export const staticIpModuleOptions = chariotModuleOptions.filter(
  o => o.value === FlagType.DISABLE_STATIC_IP_ADDRESSES
);

/** The AI feature toggles, rendered under the "AI Features" header. */
export const aiModuleOptions = chariotModuleOptions.filter(
  o => o.value !== FlagType.DISABLE_STATIC_IP_ADDRESSES
);

/** Recursively collect all options that depend (directly or transitively) on a given flag */
export const collectDependents = (
  flagValue: FlagType
): ChariotModuleOption[] => {
  const directDependents = chariotModuleOptions.filter(
    opt => opt.dependsOn === flagValue
  );
  return directDependents.flatMap(dep => [
    dep,
    ...collectDependents(dep.value),
  ]);
};
